/**
 * Settings dialog — account management.
 *
 * A native `dialog` element gives us the focus trap, Escape handling, and
 * background inertness for free; no focus-management library is involved.
 *
 * There is deliberately no "test connection" button: opening an account is
 * the test, and its result is the folder list (or the failure note beside
 * it). A green tick here would promise more than a TCP login proves.
 */
import type { JSX } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";

import { addAccount, removeAccount } from "../state/accounts.ts";
import { accountRemoved, activateAccount } from "../state/session.ts";
import { accounts, activeAccountId } from "../state/store.ts";

import { CloseIcon } from "./icons.tsx";

/**
Props of the settings dialog.
 */
export interface SettingsDialogProps {
  /**
  Whether the dialog should be open.
   */
  readonly open: boolean;
  /**
  Called when the user closes the dialog.
   */
  readonly onClose: () => void;
}

/**
 * Renders the account manager as a modal dialog.
 *
 * @param props - Open flag and close callback.
 * @returns The dialog element.
 */
export function SettingsDialog(props: SettingsDialogProps): JSX.Element {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog === null) {
      return;
    }
    if (props.open && !dialog.open) {
      dialog.showModal();
    } else if (!props.open && dialog.open) {
      dialog.close();
    }
  }, [props.open]);

  return (
    <dialog
      class="settings-dialog"
      aria-labelledby="settings-title"
      ref={dialogRef}
      onClose={() => {
        props.onClose();
      }}
    >
      <div class="dialog-header">
        <h2 id="settings-title">Settings</h2>
        <button
          type="button"
          class="icon-button"
          onClick={() => {
            props.onClose();
          }}
        >
          <CloseIcon title="Close settings" />
        </button>
      </div>
      <div class="dialog-body">
        <AccountList />
        <AddAccountForm onAdded={props.onClose} />
      </div>
    </dialog>
  );
}

/**
 * Lists configured accounts with selection and removal controls.
 *
 * @returns The account list element.
 */
function AccountList(): JSX.Element {
  if (accounts.value.length === 0) {
    return (
      <p class="settings-note">No accounts configured on this device yet.</p>
    );
  }
  return (
    <ul class="account-list" aria-label="Configured accounts">
      {accounts.value.map((account) => (
        <li class="account-row" key={account.id}>
          <span class="account-label">
            {account.label}
            <span class="account-email"> {account.email}</span>
          </span>
          <span class="account-actions">
            {activeAccountId.value === account.id ? (
              <span class="account-active">Active</span>
            ) : (
              <button
                type="button"
                class="ghost-button"
                onClick={() => {
                  void activateAccount(account.id);
                }}
              >
                Use
              </button>
            )}
            <button
              type="button"
              class="ghost-button danger"
              onClick={() => {
                void discardAccount(account.id, account.label);
              }}
            >
              Remove
            </button>
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Removes an account after an explicit confirmation, clearing its offline
 * cache and falling back to another account when it was active.
 *
 * @param id - Account id to delete.
 * @param label - Display label used in the confirmation text.
 */
async function discardAccount(id: string, label: string): Promise<void> {
  const shouldRemove = confirm(
    `Remove ${label} from this device? Its saved password is deleted too.`,
  );
  if (shouldRemove) {
    await removeAccount(id);
    await accountRemoved(id);
  }
}

/**
 * Reads a string field from a form submission.
 *
 * @param data - The submitted form data.
 * @param name - Field name.
 * @returns The field's string value, or `''` for non-string entries.
 */
function fieldText(data: FormData, name: string): string {
  const value = data.get(name);
  return typeof value === "string" ? value : "";
}

/**
Parsed and validated add-account submission.
 */
interface ValidSubmission {
  /**
  Validation error message, or `null` when the form is usable.
   */
  readonly error: string | null;
  /**
  Display label; falls back to the email address.
   */
  readonly label: string;
  /**
  Primary email address.
   */
  readonly email: string;
  /**
  IMAP hostname.
   */
  readonly imapHost: string;
  /**
  IMAP port.
   */
  readonly imapPort: number;
  /**
  SMTP hostname.
   */
  readonly smtpHost: string;
  /**
  SMTP port.
   */
  readonly smtpPort: number;
  /**
  Login user name; falls back to the email address.
   */
  readonly username: string;
  /**
  Plaintext password.
   */
  readonly password: string;
}

/**
 * Validates the add-account form values.
 *
 * @param data - Submitted form data.
 * @returns The parsed submission, or the first validation error.
 */
function validateSubmission(data: FormData): ValidSubmission {
  const text = (name: string): string => fieldText(data, name).trim();
  const port = (name: string): number => {
    const parsed = Number(text(name));
    return Number.isSafeInteger(parsed) && parsed >= 1 && parsed <= 65_535
      ? parsed
      : 0;
  };
  const email = text("email");
  const password = fieldText(data, "password");
  const imapPort = port("imapPort");
  const smtpPort = port("smtpPort");
  const fallback = (value: string): string => (value === "" ? email : value);
  if (email === "" || !email.includes("@")) {
    return invalid("Enter a full email address, e.g. you@example.com.");
  }
  if (imapPort < 1 || smtpPort < 1) {
    return invalid("Ports must be whole numbers between 1 and 65535.");
  }
  if (text("imapHost") === "" || text("smtpHost") === "") {
    return invalid("Enter the IMAP and SMTP server names.");
  }
  if (password === "") {
    return invalid("Enter the account password.");
  }
  return {
    error: null,
    label: fallback(text("label")),
    email,
    imapHost: text("imapHost"),
    imapPort,
    smtpHost: text("smtpHost"),
    smtpPort,
    username: fallback(text("username")),
    password,
  };
}

/**
 * Builds an invalid submission carrying one message.
 *
 * @param message - Human-readable validation error.
 * @returns A submission whose error is set and fields are empty.
 */
function invalid(message: string): ValidSubmission {
  return {
    error: message,
    label: "",
    email: "",
    imapHost: "",
    imapPort: 0,
    smtpHost: "",
    smtpPort: 0,
    username: "",
    password: "",
  };
}

/**
 * The add-account form: local state only, submitted to encrypted storage.
 *
 * @param fields - Callback invoked after an account is saved.
 * @param fields.onAdded - Invoked after an account is saved.
 * @returns The form element.
 */
function AddAccountForm(fields: { readonly onAdded: () => void }): JSX.Element {
  const [error, setError] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  /**
   * Validates and persists the form, then hands control back to the caller.
   *
   * @param form - The submitted form element.
   */
  const submit = (form: HTMLFormElement): void => {
    const submission = validateSubmission(new FormData(form));
    if (submission.error !== null) {
      setError(submission.error);
      return;
    }
    setError(null);
    void (async () => {
      try {
        const saved = await addAccount({
          label: submission.label,
          email: submission.email,
          imapHost: submission.imapHost,
          imapPort: submission.imapPort,
          smtpHost: submission.smtpHost,
          smtpPort: submission.smtpPort,
          username: submission.username,
          password: submission.password,
        });
        // Opening the account immediately doubles as the connection test:
        // its outcome lands in the folder list or the sidebar's error note.
        await activateAccount(saved.id);
        form.reset();
        fields.onAdded();
      } catch (error: unknown) {
        setError(
          error instanceof Error ? error.message : "Saving the account failed.",
        );
      }
    })();
  };

  /**
   * Fills the server fields from the documented DNS convention: InterServer
   * exposes `mail.<domain>` for both IMAP (993) and SMTP (465).
   */
  const fillServerDefaults = (): void => {
    const form = formRef.current;
    if (form === null) {
      return;
    }
    const email = fieldText(new FormData(form), "email");
    const domain = email.slice(email.lastIndexOf("@") + 1).trim();
    if (domain === "" || !domain.includes(".")) {
      setError(
        "Enter the email address first; the servers follow mail.<your-domain>.",
      );
      return;
    }
    setField(form, "imapHost", `mail.${domain}`);
    setField(form, "smtpHost", `mail.${domain}`);
    setField(form, "imapPort", "993");
    setField(form, "smtpPort", "465");
    setField(form, "username", email);
    setError(null);
  };

  return (
    <form
      class="add-account"
      ref={formRef}
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        submit(event.currentTarget);
      }}
    >
      <h3 class="section-title">Add an account</h3>
      <div class="field-grid">
        <label class="field">
          <span>Label</span>
          <input
            name="label"
            type="text"
            placeholder="Personal mail"
            autoComplete="off"
          />
        </label>
        <label class="field">
          <span>Email</span>
          <input
            name="email"
            type="email"
            required
            placeholder="you@example.com"
            autoComplete="off"
          />
        </label>
        <label class="field">
          <span>User name</span>
          <input
            name="username"
            type="text"
            placeholder="usually the email address"
            autoComplete="off"
          />
        </label>
        <label class="field">
          <span>Password</span>
          <input
            name="password"
            type="password"
            required
            autoComplete="new-password"
          />
        </label>
        <label class="field">
          <span>IMAP server</span>
          <input
            name="imapHost"
            type="text"
            required
            placeholder="mail.example.com"
            autoComplete="off"
          />
        </label>
        <label class="field">
          <span>IMAP port</span>
          <input
            name="imapPort"
            type="number"
            min={1}
            max={65_535}
            required
            defaultValue={993}
          />
        </label>
        <label class="field">
          <span>SMTP server</span>
          <input
            name="smtpHost"
            type="text"
            required
            placeholder="mail.example.com"
            autoComplete="off"
          />
        </label>
        <label class="field">
          <span>SMTP port</span>
          <input
            name="smtpPort"
            type="number"
            min={1}
            max={65_535}
            required
            defaultValue={465}
          />
        </label>
      </div>
      <p class="settings-note">
        IMAP is used (not POP3) so messages stay on the server. Ports 993 and
        465 use implicit TLS.
      </p>
      <div class="form-actions">
        <button
          type="button"
          class="ghost-button"
          onClick={(event) => {
            event.preventDefault();
            fillServerDefaults();
          }}
        >
          Fill mail.&lt;domain&gt; defaults
        </button>
        <button type="submit" class="primary-button">
          Save account
        </button>
      </div>
      {error === null ? null : (
        <p class="form-error" role="alert">
          {error}
        </p>
      )}
    </form>
  );
}

/**
 * Sets a named field's value inside a form.
 *
 * @param form - The owning form.
 * @param name - Field name.
 * @param value - Value to assign.
 */
function setField(form: HTMLFormElement, name: string, value: string): void {
  const field = form.elements.namedItem(name);
  if (field instanceof HTMLInputElement) {
    field.value = value;
  }
}
